/**
 * Finance API Hook
 * 
 * Centralized API wrapper for all finance module operations
 * Handles authentication, error handling, and provides convenient methods
 */

import { useState, useCallback, useMemo } from 'react';

interface FinanceAPIRequest {
  action?: 'read' | 'list' | 'create' | 'update' | 'delete' | 'count' | 
          // Unified actions (new)
          'generateDocumentNumber' | 'convertDocument' | 'sendDocument' | 'markDocumentAsPaid' |
          // Legacy actions (backward compatibility)
          'generateInvoiceNumber' | 'generateQuoteNumber' | 'generateCreditNoteNumber' |
          'generatePurchaseOrderNumber' | 'generateBillNumber' | 'generateGoodsReceivedNumber' |
          'generateSupplierCreditNumber' |
          'calculateTotals' | 'reconcilePayment' | 
          'getDashboardStats' | 
          'convertQuoteToInvoice' | 'convertInvoiceToCreditNote' |
          'convertPurchaseOrderToBill' | 'convertPurchaseOrderToGoodsReceived' |
          'applySupplierCreditToBill' |
          // Supplier Credit actions
          'getSupplierCreditNotes' | 'getSupplierCreditNote' | 'getSupplierCreditItems' |
          'saveSupplierCreditNote' | 'deleteSupplierCreditNote' | 'updateSupplierCreditStatus' |
          'getSupplierCreditApplications' | 'removeSupplierCreditApplication' | 'getSupplierCreditStats';
    endpoint?: string; 
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; 
  table?: string;
  where?: any;
  data?: any;
  fields?: string[];
  orderBy?: { field: string; direction: 'asc' | 'desc' }[];
  limit?: number;
  offset?: number;
  page?: number;
  pageSize?: number;
  search?: string; // Search term
  searchFields?: string[]; // Fields to search across (OR condition)
  document_type?: 'invoice' | 'quote' | 'credit_note' | 'purchase_order' | 'supplier_invoice' | 'goods_received' | 'supplier_credit' | 'delivery_note' | 'proforma_invoice';
}

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  stats?: any;
  invoice_number?: string;
  totals?: any;
  count?: number;
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ListResponse<T> {
  data: T[];
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export function useFinanceAPI() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = useCallback(() => {
    try {
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        const tokens = JSON.parse(storedTokens);
        return tokens.access_token;
      }
    } catch (error) {
      console.error('[useFinanceAPI] Failed to load auth token:', error);
    }
    return null;
  }, []);

  const callAPI = useCallback(async <T = any>(request: FinanceAPIRequest): Promise<T | null> => {
    setLoading(true);
    setError(null);

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('No authentication token available');
      }

      const response = await fetch('/api/v1/finance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        // Parse the error body so callers (DocumentModal, etc.) can react to
        // server codes such as CREDIT_LIMIT_EXCEEDED or batch_locked instead
        // of just getting a generic HTTP status message.
        let parsed: any = null;
        try { parsed = await response.json(); } catch { parsed = null; }
        const message = parsed?.error || parsed?.message || `HTTP error! status: ${response.status}`;
        const apiError: Error & { status?: number; code?: string; details?: unknown } = new Error(message);
        apiError.status = response.status;
        if (parsed?.code) apiError.code = parsed.code;
        if (parsed?.details) apiError.details = parsed.details;
        throw apiError;
      }

      const result: APIResponse<T> = await response.json();

      if (!result.success) {
        const apiError: Error & { status?: number; code?: string; details?: unknown } = new Error(result.error || 'API request failed');
        apiError.status = response.status;
        if ((result as any).code) apiError.code = (result as any).code;
        if ((result as any).details) apiError.details = (result as any).details;
        throw apiError;
      }

      // For list actions, return both data and pagination
      if (request.action === 'list' && result.pagination) {
        return {
          data: result.data || [],
          pagination: result.pagination
        } as any;
      }

      // Return the appropriate data field
      return (result.data || result.stats || result) as T;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('[useFinanceAPI] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  // Convenience methods
  const list = useCallback(<T = any>(table: string, options?: Partial<FinanceAPIRequest>) => {
    return callAPI<ListResponse<T>>({ action: 'list', table, ...options });
  }, [callAPI]);

  const read = useCallback(<T = any>(table: string, where: any, fields?: string[]) => {
    return callAPI<T>({ action: 'read', table, where, fields });
  }, [callAPI]);

  const create = useCallback(<T = any>(table: string, data: any) => {
    return callAPI<T>({ action: 'create', table, data });
  }, [callAPI]);

  const update = useCallback(<T = any>(table: string, where: any, data: any) => {
    return callAPI<T>({ action: 'update', table, where, data });
  }, [callAPI]);

  const remove = useCallback((table: string, where: any) => {
    return callAPI({ action: 'delete', table, where });
  }, [callAPI]);

  const count = useCallback((table: string, where?: any) => {
    return callAPI<{ count: number }>({ action: 'count', table, where });
  }, [callAPI]);

  const generateInvoiceNumber = useCallback(() => {
    return callAPI<{ invoice_number: string }>({ action: 'generateInvoiceNumber' });
  }, [callAPI]);

  const calculateTotals = useCallback((items: any[]) => {
    return callAPI<{ subtotal: number; tax_total: number; total: number }>({ 
      action: 'calculateTotals', 
      data: { items } 
    });
  }, [callAPI]);

  const reconcilePayment = useCallback((paymentId: number, allocations: any[]) => {
    return callAPI({ 
      action: 'reconcilePayment', 
      data: { payment_id: paymentId, allocations } 
    });
  }, [callAPI]);

  const getDashboardStats = useCallback(() => {
    return callAPI({ action: 'getDashboardStats' });
  }, [callAPI]);

  return useMemo(() => ({
    loading,
    error,
    callAPI,
    list,
    read,
    create,
    update,
    remove,
    count,
    generateInvoiceNumber,
    calculateTotals,
    reconcilePayment,
    getDashboardStats
  }), [
    loading,
    error,
    callAPI,
    list,
    read,
    create,
    update,
    remove,
    count,
    generateInvoiceNumber,
    calculateTotals,
    reconcilePayment,
    getDashboardStats
  ]);
}
